import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  Customer,
  CustomerAddress,
  CustomerCard,
  CustomerDocumentType,
  CustomerOrder,
  CustomerRegistrationStage,
} from '../../../models/customer.model';
import { CustomerService } from '../../../services/customer.service';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { PhoneFormatPipe } from '../../../../shared/pipes/format-phone.pipe';
import { ToolbarModule } from 'primeng/toolbar';
import { TabsModule } from 'primeng/tabs';
import { TagStatusComponent } from '../../../../shared/components/tag-status/tag-status.component';
import { OrderStatus } from '../../../models/order.model';
import { OrderService } from '../../../services/order.service';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-customer-details',
  standalone: true,
  templateUrl: './customer-details.component.html',
  imports: [
    BreadcrumbModule,
    ButtonModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    DatePipe,
    ToolbarModule,
    TabsModule,
    TagStatusComponent,
    RouterLink,
    TitleCasePipe,
    DatePipe,
    PaginatorModule,
  ],
})
export class CustomerDetailsComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private customerService = inject(CustomerService);
  private messageService = inject(MessageService);
  private orderService = inject(OrderService);
  private phoneFormatPipe = new PhoneFormatPipe();

  customer = signal<Customer | null>(null);
  orders = computed<CustomerOrder[]>(() => this.customer()?.orders ?? []);
  loading = signal(false);

  readonly first = signal(0); // index of first record
  readonly rows = signal(5);

  readonly pagedOrders = computed(() => {
    const list = this.orders() ?? [];
    const start = this.first();
    const end = start + this.rows();
    return list.slice(start, end);
  });

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');
    if (id) this.loadData(id);
  }

  get primaryEmail(): string | null {
    const c = this.customer();
    if (!c || !c.electronicAddresses || c.electronicAddresses.length === 0) {
      return null;
    }
    return c.electronicAddresses[0]?.address ?? null;
  }

  get primaryPhone(): string | null {
    const c = this.customer();
    if (!c || !c.phones || c.phones.length === 0) {
      return null;
    }
    return this.phoneFormatPipe.transform(
      c.phones[0]?.number ?? '',
      c.phones[0]?.countryCode ?? '',
      { includeCountryCode: true },
    );
  }

  get nationalIdDocument(): string {
    if (
      this.customer() &&
      this.customer()?.documents &&
      this.customer()!.documents!.length > 0
    ) {
      const documents = this.customer()!.documents!.filter(
        (x) =>
          Number(CustomerDocumentType[x.type]) ==
          CustomerDocumentType.NationalId,
      );
      return documents && documents.length > 0 ? documents[0].number : '-';
    }
    return '-';
  }

  get deliveryAddress(): CustomerAddress | null {
    if (
      this.customer() &&
      this.customer()?.addresses &&
      this.customer()!.addresses!.length > 0
    ) {
      return this.customer()!.addresses!.filter((x) => x.isDefault)![0] ?? null;
    }
    return null;
  }

  onPageChange(event: PaginatorState): void {
    this.first.set(event.first ?? 0);
    this.rows.set(event.rows ?? 10);
  }

  getCountryDescriptionByCotryCode(code?: string | null): string {
    if (!code) return '-';

    const country = this.customerService
      .getCountryOptions()
      .filter((x) => x.value == code.toLocaleLowerCase());
    return country && country.length > 0 ? country[0].label : '-';
  }

  getOrderStatusTag(status: OrderStatus): string {
    return this.orderService.getStatusTag(status);
  }

  getOrderStatusDescription(status: OrderStatus): string {
    return this.orderService.getStatusDescription(status);
  }

  getRegistrationStageDescription(stage: CustomerRegistrationStage): string {
    switch (Number(CustomerRegistrationStage[stage])) {
      case CustomerRegistrationStage.AccountCreated:
        return 'Conta Criada';
      case CustomerRegistrationStage.AccountValidated:
        return 'Conta Validada';
      case CustomerRegistrationStage.PasswordCreated:
        return 'Senha Criada';
      case CustomerRegistrationStage.Active:
        return 'Ativo';
      default:
        return '';
    }
  }

  formatCardExpirationDate(card: CustomerCard): string {
    return (
      String(card.expirationMonth).padStart(2, '0') + '/' + card.expirationYear
    );
  }

  private loadData(id: string): void {
    this.loading.set(true);

    this.customerService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const data = response.data as Customer;
        this.customer.set(data);
      },
      error: (e) => {
        this.loading.set(false);
        this.customer.set(null);

        if (e instanceof HttpErrorResponse && e.status !== 404) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar os dados do cliente',
            life: 3000,
          });
        }
      },
    });
  }
}
